import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, getToken, setToken } from './api/authAPI';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setAuthToken] = useState(getToken());
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const savedToken = getToken();
        if (savedToken) {
          setAuthToken(savedToken);
          try {
            const response = await authAPI.getCurrentUser();
            if (response.success) {
              setUser(response.user);
            }
          } catch (err) {
            console.error('Auth check failed:', err);
            setAuthToken(null);
            setToken(null);
            setUser(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Signup
  const signup = async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.signup(data);
      if (response.success) {
        setToken(response.token);
        setAuthToken(response.token);
        setUser(response.user);
        return response;
      }
    } catch (err) {
      const errorMsg = err.message || 'Signup failed';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login
  const login = async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.login(data);
      if (response.success) {
        setToken(response.token);
        setAuthToken(response.token);
        setUser(response.user);
        return response;
      }
    } catch (err) {
      const errorMsg = err.message || 'Login failed';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password
  const forgotPassword = async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.forgotPassword(data);
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to send reset email';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Reset Password
  const resetPassword = async (token, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.resetPassword(token, data);
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to reset password';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Change Password
  const changePassword = async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.changePassword(data);
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to change password';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = () => {
    authAPI.logout();
    setUser(null);
    setAuthToken(null);
    setToken(null);
  };

  const value = {
    user,
    token,
    loading,
    error,
    setError,
    signup,
    login,
    forgotPassword,
    resetPassword,
    changePassword,
    logout,
    isAuthenticated: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
